import XHRRequest, { XHRResponse } from "../request";

export type Filters = Record<string, { value: any; name?: string | null; type: string }>;

export interface LoadOptions {
  excludes?: string;
  searchValue?: string;
  page?: number;
  perPage?: number;
  isSearch?: boolean;
  type?: string;
  filters: Filters;
}

interface Args {
  action: string;
  onLoadStart?: () => void;
  onLoadSuccess?: (response: XHRResponse) => void;
  onLoadError?: (error: unknown) => void;
  options: LoadOptions;
}

type ListEventMap = {
  start: { options: LoadOptions; page: number; isLoadingMore: boolean };
  success: { response: XHRResponse; page: number; isLoadingMore: boolean };
  error: { error: unknown; page: number; isLoadingMore: boolean };
};

type ListEvent = keyof ListEventMap;
type ListEventListener<K extends ListEvent> = (payload: ListEventMap[K]) => void;

/**
 * Manage async post list with load more
 */
export default class AsyncListManager {
  //
  currentPage: number;
  isLoadingMore = false;
  action: Args["action"];
  options: Args["options"] = { page: 1, filters: {} };
  private listeners: { [K in ListEvent]: Set<ListEventListener<K>> } = {
    start: new Set(),
    success: new Set(),
    error: new Set(),
  };

  constructor(args: Args) {
    this.action = args.action;
    this.options = args.options;
    this.currentPage = args.options.page || 1;

    // Keep existing callback API by wiring callbacks into events.
    if (args.onLoadStart) {
      this.subscribe("start", () => args.onLoadStart?.());
    }
    if (args.onLoadSuccess) {
      this.subscribe("success", ({ response }) => args.onLoadSuccess?.(response));
    }
    if (args.onLoadError) {
      this.subscribe("error", ({ error }) => args.onLoadError?.(error));
    }
  }

  toQueryString(withPage: boolean = false) {
    const qs = withPage && this.currentPage > 1 ? `pa=${this.currentPage}` : "";
    const filterQs = Object.entries(this.options.filters).reduce((acc, [key, { value }]) => {
      if (value) {
        acc += `&${key}=${value}`;
      }
      return acc;
    }, "");
    const searchQs = this.options.searchValue ? `&s=${encodeURIComponent(this.options.searchValue)}` : "";
    return (qs + filterQs + searchQs).replace(/^&/, ""); // Remove leading & if present
  }

  subscribe<K extends ListEvent>(event: K, listener: ListEventListener<K>) {
    this.listeners[event].add(listener as ListEventListener<any>);
    return () => {
      this.listeners[event].delete(listener as ListEventListener<any>);
    };
  }

  private emit<K extends ListEvent>(event: K, payload: ListEventMap[K]) {
    this.listeners[event].forEach((listener) => {
      try {
        (listener as ListEventListener<K>)(payload);
      } catch (error) {
        console.error(`[AsyncListManager] listener error on "${event}"`, error);
      }
    });
  }

  private handleXHRSuccess(response: XHRResponse, page: number) {
    setTimeout(() => {
      document.body.classList.remove("loading");
    }, 100);

    if (response.data.hasOwnProperty("has_results")) {
      if (response.data.has_results === false && this.isLoadingMore === false) {
        this.currentPage = 1;
      }

      this.emit("success", {
        response,
        page,
        isLoadingMore: this.isLoadingMore,
      });
    }
  }

  private handleXHRError(error: unknown, page: number) {
    setTimeout(() => {
      document.body.classList.remove("loading");
    }, 100);

    this.emit("error", {
      error,
      page,
      isLoadingMore: this.isLoadingMore,
    });
  }

  setOption<T extends keyof LoadOptions>(key: T, value: LoadOptions[T]) {
    this.options[key] = value;
  }

  //  Set or update a filter value
  setFilter(key: string | null, { value, name, type }: Filters[string]) {
    if (!key) return;
    this.options.filters[key] = { value: value === "all" ? null : value, name, type };
  }

  loadMore() {
    document.body.classList.add("loading");
    this.isLoadingMore = true;
    this.currentPage++;
    this.makeRequest(this.options, this.currentPage);
  }

  load() {
    document.body.classList.add("loading");
    this.isLoadingMore = false;
    this.makeRequest(this.options, this.options.page || 1);
  }

  private makeRequest(options: LoadOptions, page: number) {
    console.log(options);
    this.emit("start", {
      options,
      page,
      isLoadingMore: this.isLoadingMore,
    });

    XHRRequest.fetch({
      url: "/wp/wp-admin/admin-ajax.php?action=" + this.action,
      data: { ...options, page },
      onSuccess: (response: XHRResponse) => this.handleXHRSuccess(response, page),
      onError: (error: unknown) => this.handleXHRError(error, page),
    });
  }
}
